import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Tag } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  RightOutlined,
  RocketOutlined,
  BulbOutlined,
  WarningOutlined,
  MessageOutlined,
  ThunderboltFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { eventBus, EVENTS } from '../../utils/eventBus';
import welcomeStorage from '../../utils/welcomeStorage';
import useUserStore from '../../store/userStore';
import './BetaWelcomeModal.css';

/**
 * BetaWelcomeModal
 * -----------------------------------------------------------------------------
 * Modal hiển thị ngay SAU khi user đăng ký thành công hoặc lần đầu đăng nhập
 * bằng Facebook/Google trên trình duyệt.
 *
 * Triển khai đúng theo spec:
 *   `05_Beta_Onboarding_Support_Feedback_FINAL/
 *    CANSLIM_Beta_Welcome_Getting_Started_Page_FE_Spec_FIX1_*.md`
 *
 * 7 sections:
 *   1. Welcome hero
 *   2. Product expectation (fit / non-fit)
 *   3. 5-minute first workflow
 *   4. Score/Grade quick explanation
 *   5. What not to misunderstand
 *   6. Feedback / Zalo group
 *   7. Main CTA
 *
 * Flow:
 *   - Hiển thị tự động khi user vừa đăng ký / social-login lần đầu
 *     (App.jsx đọc welcomeStorage.consumeJustRegistered()).
 *   - User có thể "Bỏ qua" hoặc "Bắt đầu ngay" — cả hai đều đánh dấu đã xem.
 *   - Không hiện lại trên cùng trình duyệt / cùng user.
 */

const WORKFLOW_STEPS = [
  {
    title: 'Xem Market Dashboard',
    description:
      'Đọc giai đoạn thị trường, mức rủi ro, chính sách danh mục, cảnh báo và nhóm ngành dẫn đầu.',
    href: 'https://stockinsight.vn/breakout-tool',
  },
  {
    title: 'Chọn 1 câu hỏi trong Question Engine',
    description:
      'Bắt đầu bằng một câu hỏi CANSLIM / breakout / swing thay vì tự dựng quá nhiều bộ lọc.',
  },
  {
    title: 'Review 1–3 mã trong Shortlist',
    description:
      'Shortlist là danh sách để review, không phải danh sách mua.',
  },
  {
    title: 'Xem Score/Grade và AI note',
    description:
      'Score/Grade cho biết mã/tín hiệu nổi bật ra sao so với thị trường hoặc nhóm ngành. AI note giúp diễn giải điểm mạnh và rủi ro.',
  },
  {
    title: 'Thêm 1 mã vào Watchlist',
    description:
      'Nếu bạn muốn theo dõi tiếp, hãy thêm mã vào Watchlist. Khi có tín hiệu quan trọng hoặc rủi ro, app sẽ nhắc qua chuông in-app.',
  },
  {
    title: 'Mở Stock Detail nếu cần',
    description:
      'Xem chart, CANSLIM Checklist và các lớp dữ liệu chi tiết hơn.',
  },
  {
    title: 'Ghi thử Trade Plan',
    description:
      'Nếu muốn theo dõi nghiêm túc, hãy ghi lý do, entry, stop, target, tỷ trọng và rủi ro tối đa.',
  },
];

const MISCONCEPTIONS = [
  {
    label: 'Shortlist / Pick 3',
    text: 'Shortlist và Pick 3 là danh sách để review, không phải danh sách mua.',
  },
  {
    label: 'A+',
    text: 'A+ là nhóm rất nổi bật theo percentile, không phải lệnh mua.',
  },
  {
    label: 'High-N',
    text: 'High-N là breakout đáng review, không phải lệnh mua.',
  },
  {
    label: 'Low-N',
    text: 'Low-N là cảnh báo rủi ro, không phải tín hiệu short.',
  },
  {
    label: 'AI note',
    text: 'AI note diễn giải dữ liệu hệ thống, không phải AI báo mua.',
  },
  {
    label: 'Mẫu nến',
    text: 'Mẫu nến là tín hiệu bối cảnh để review thêm, không phải lệnh mua/bán riêng lẻ.',
  },
];

const BetaWelcomeModal = () => {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const [open, setOpen] = useState(false);
  // Track userId đã xử lý bằng ref để effect có thể chạy lại khi user thay đổi
  // (vd: logout rồi login user khác) mà không bị block bởi state cũ.
  const processedUserIdRef = useRef(null);

  // Lắng nghe logout: reset ref để user mới (login lại) trigger lại effect
  useEffect(() => {
    const unsubscribe = eventBus.on(EVENTS.USER_LOGOUT, () => {
      processedUserIdRef.current = null;
      setOpen(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    // Bỏ qua nếu chưa có user hoặc user này đã được xử lý rồi
    if (!user || !user.id) return;
    if (processedUserIdRef.current === user.id) return;

    const userId = user.id;
    // Dùng peek (không xóa) để effect có thể chạy lại nếu React render trước
    // khi user state được set xong — tránh race condition giữa setUser() và
    // markJustRegistered().
    const justRegistered = welcomeStorage.peekJustRegistered();
    const hasSeen = welcomeStorage.hasSeenWelcome(userId);

    if (justRegistered && !hasSeen) {
      setOpen(true);
      // Sau khi chắc chắn mở modal, consume flag để tránh hiện lại khi F5
      welcomeStorage.consumeJustRegistered();
    }
    // Đánh dấu user này đã xử lý — dù modal mở hay không
    processedUserIdRef.current = user.id;
  }, [user]);

  const persistSeenAndClose = () => {
    welcomeStorage.markSeen(user?.id);
    setOpen(false);
  };

  const handleStartNow = () => {
    persistSeenAndClose();
  };

  const handleSkip = () => {
    persistSeenAndClose();
  };

  const handleOpenFeedback = () => {
    // Mở FeedbackPopup global qua event bus
    eventBus.emit(EVENTS.OPEN_FEEDBACK_POPUP);
    persistSeenAndClose();
  };

  const handleGoBreakout = () => {
    persistSeenAndClose();
    navigate('/breakout-tool');
  };

  return (
    <Modal
      open={open}
      onCancel={handleSkip}
      footer={null}
      width={760}
      centered
      destroyOnClose
      maskClosable={false}
      className="beta-welcome-modal"
      title={null}
      closeIcon={<span aria-label="Đóng">×</span>}
    >
      {/* ==================== SECTION 1: HERO ==================== */}
      <div className="bw-hero">
        <div className="bw-hero-icon">
          <RocketOutlined />
        </div>
        <h1 className="bw-hero-title">
          Chào mừng bạn đến với Stock Insight AI beta
        </h1>
        <p className="bw-hero-subtitle">
          Đây là workspace hỗ trợ bạn review thị trường và cổ phiếu theo
          CANSLIM / breakout / swing có dữ liệu, bối cảnh và kỷ luật hơn.
        </p>
        <Button
          type="primary"
          size="large"
          icon={<RightOutlined />}
          onClick={handleStartNow}
          className="bw-cta-primary"
        >
          Bắt đầu workflow 5 phút
        </Button>
      </div>

      {/* ==================== SECTION 2: EXPECTATION ==================== */}
      <section className="bw-section">
        <h2 className="bw-section-title">
          <BulbOutlined /> Trước khi bắt đầu, hãy hiểu đúng sản phẩm
        </h2>
        <p className="bw-section-body">
          Stock Insight AI <strong>không phải dịch vụ phím hàng</strong>, không
          bán “3 mã để mua” và <strong>không cam kết lợi nhuận</strong>.
        </p>
        <p className="bw-section-body">
          Sản phẩm được thiết kế để hỗ trợ bạn tự review: đọc bối cảnh thị
          trường, xem shortlist, hiểu Score/Grade, đọc AI note, theo dõi
          Watchlist/cảnh báo và ghi Trade Plan cá nhân trước khi ra quyết định.
        </p>

        <div className="bw-fit-grid">
          <div className="bw-fit-card bw-fit-yes">
            <h4>
              <CheckCircleFilled /> Phù hợp nếu
            </h4>
            <ul>
              <li>Bạn muốn tự review cổ phiếu có dữ liệu hơn.</li>
              <li>Bạn quan tâm CANSLIM / breakout / swing.</li>
              <li>
                Bạn muốn biết mã/tín hiệu nào nổi bật hơn thị trường hoặc
                nhóm ngành.
              </li>
              <li>Bạn muốn theo dõi Watchlist và rủi ro rõ hơn.</li>
            </ul>
          </div>
          <div className="bw-fit-card bw-fit-no">
            <h4>
              <CloseCircleFilled /> Có thể không phù hợp nếu
            </h4>
            <ul>
              <li>Bạn chỉ muốn nhận mã mua sẵn.</li>
              <li>Bạn kỳ vọng công cụ đảm bảo lợi nhuận.</li>
              <li>Bạn không muốn đọc dữ liệu, bối cảnh hoặc rủi ro.</li>
              <li>Bạn muốn app quyết định mua/bán thay mình.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 3: 5-MIN WORKFLOW ==================== */}
      <section className="bw-section" id="bw-first-workflow">
        <h2 className="bw-section-title">
          <ThunderboltFilled /> 5 phút đầu nên làm gì?
        </h2>
        <p className="bw-section-body">
          Bạn không cần hiểu hết mọi thuật ngữ ngay. Hãy đi qua workflow ngắn
          này trước.
        </p>
        <ol className="bw-workflow-list">
          {WORKFLOW_STEPS.map((step, idx) => (
            <li key={step.title} className="bw-workflow-item">
              <div className="bw-workflow-num">{idx + 1}</div>
              <div className="bw-workflow-body">
                <div className="bw-workflow-title">{step.title}</div>
                <div className="bw-workflow-desc">{step.description}</div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ==================== SECTION 4: SCORE/GRADE ==================== */}
      <section className="bw-section">
        <h2 className="bw-section-title">
          <BulbOutlined /> Score/Grade trong app nghĩa là gì?
        </h2>
        <p className="bw-section-body">
          <strong>Score</strong> là điểm trên thang 100. <strong>Grade</strong> là
          phân hạng tương đối theo percentile như A+, A, B, C, D, E.
        </p>
        <div className="bw-score-chips">
          <Tag color="green">A+ ≥ 97</Tag>
          <Tag color="green">A ≥ 90</Tag>
          <Tag color="cyan">B ≥ 80</Tag>
          <Tag color="blue">C ≥ 70</Tag>
          <Tag color="orange">D ≥ 60</Tag>
          <Tag>E &lt; 60</Tag>
        </div>
        <p className="bw-section-body">
          <strong>Market grade</strong> cho biết mã/tín hiệu nổi bật thế nào so
          với toàn thị trường. <strong>Industry grade</strong> cho biết mã/tín
          hiệu nổi bật thế nào so với các mã cùng ngành.
        </p>
        <div className="bw-score-disclaimer">
          ⚠️ Score/Grade giúp ưu tiên review, không phải khuyến nghị mua/bán và
          không cam kết lợi nhuận.
        </div>
      </section>

      {/* ==================== SECTION 5: WHAT NOT TO MISUNDERSTAND ==================== */}
      <section className="bw-section">
        <h2 className="bw-section-title">
          <WarningOutlined /> Một số điểm dễ hiểu nhầm
        </h2>
        <ul className="bw-misconception-list">
          {MISCONCEPTIONS.map((m) => (
            <li key={m.label}>
              <span className="bw-mis-label">{m.label}</span>
              <span className="bw-mis-text">{m.text}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ==================== SECTION 6: FEEDBACK / ZALO ==================== */}
      <section className="bw-section">
        <h2 className="bw-section-title">
          <MessageOutlined /> Beta cần feedback thật của bạn
        </h2>
        <p className="bw-section-body">
          Nếu có phần nào khó hiểu, lỗi giao diện, thuật ngữ gây nhầm lẫn
          hoặc tính năng bạn muốn cải thiện, hãy gửi feedback cho mình.
        </p>
        <Button icon={<MessageOutlined />} onClick={handleOpenFeedback}>
          Gửi feedback beta
        </Button>
      </section>

      {/* ==================== SECTION 7: MAIN CTA ==================== */}
      <div className="bw-footer-cta">
        <h3>Sẵn sàng bắt đầu chưa?</h3>
        <div className="bw-footer-cta-buttons">
          <Button type="primary" size="large" onClick={handleGoBreakout}>
            Vào Breakout Tool
          </Button>
          <Button size="large" onClick={handleOpenFeedback}>
            Gửi feedback sau khi dùng thử
          </Button>
        </div>
        <p className="bw-footer-disclaimer">
          Không phím hàng. Không cam kết lợi nhuận. Không thay thế quyết định
          đầu tư cá nhân.
        </p>
      </div>
    </Modal>
  );
};

export default BetaWelcomeModal;
